import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Icon } from '@/components/icons';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { useOffer } from '@/context/OfferContext';
import { ScrollReveal } from '@/components/common/Primitives';
import { ServiceVisual, ServiceVisualSecondary } from '@/components/visuals/ITVisuals';
import { Tilt } from '@/components/experience/Experience';

export default function ServicePage() {
  const { slug } = useParams();
  const { t, tv } = useLang();
  const { services } = useServices();
  const { openOffer } = useOffer();
  const navigate = useNavigate();
  const [service, setService] = useState(null);

  useEffect(() => {
    const s = services.find((x) => x.slug === slug);
    setService(s || null);
    window.scrollTo(0, 0);
  }, [slug, services]);

  if (!service) {
    return <div className="min-h-screen grid place-items-center text-[color:var(--itw-text-2)] font-mono">// loading service...</div>;
  }

  const accent = service.accent || '#00E7FF';
  const idx = services.findIndex((s) => s.slug === slug);
  const flip = idx % 2 === 1;
  const related = services.filter((s) => s.slug !== slug).slice(0, 3);
  const num = (n) => <span className="font-mono text-lg mr-2" style={{ color: accent }}>{n}</span>;

  return (
    <div className="itw-noise" style={{ '--accent': accent }}>
      {/* HERO */}
      <section data-testid="service-hero-section" className="relative gradient-hero pt-28 lg:pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 itw-grid opacity-40" />
        <div className="absolute top-1/4 right-[6%] h-[440px] w-[440px] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-sm text-[color:var(--itw-text-2)] hover:text-[color:var(--accent)] transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t('service.backToServices')}
          </Link>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <div className={flip ? 'lg:order-2' : ''}>
              <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                className="h-16 w-16 rounded-2xl bg-[color:var(--itw-surface-2)] grid place-items-center"
                style={{ border: `1px solid ${accent}`, boxShadow: `0 0 28px ${accent}44` }}>
                <Icon name={service.icon} className="h-8 w-8" style={{ color: accent }} />
              </motion.div>
              <div className="mt-5 font-mono text-xs uppercase tracking-[0.3em]" style={{ color: accent }}>// {service.slug}</div>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[color:var(--itw-text)]">{tv(service.name)}</motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
                className="mt-4 text-lg" style={{ color: accent }}>{tv(service.tagline)}</motion.p>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
                className="mt-4 text-base text-[color:var(--itw-text-2)] leading-relaxed">{tv(service.intro)}</motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }} className="mt-8">
                <Button onClick={() => openOffer(service.slug)} data-testid="service-cta-primary"
                  className="h-12 px-6 font-medium hover:opacity-90 transition-opacity rounded-full text-[color:var(--itw-bg)]"
                  style={{ backgroundColor: accent, boxShadow: `0 0 28px ${accent}55` }}>
                  {t('nav.requestOffer')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.7, delay: 0.3 }} className={flip ? 'lg:order-1' : ''}>
              <Tilt max={7}>
                <ServiceVisual slug={service.slug} accent={accent} />
              </Tilt>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[color:var(--itw-bg)] to-transparent" />
      </section>

      {/* OUTCOMES */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{num('01.')}{t('service.outcomes')}</h2></ScrollReveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {tv(service.outcomes).map((o, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full flex items-start gap-3 transition-transform duration-200 hover:-translate-y-1" style={{ borderColor: `${accent}22` }}>
                  <div className="h-8 w-8 shrink-0 rounded-lg grid place-items-center" style={{ backgroundColor: `${accent}18`, border: `1px solid ${accent}55` }}><Check className="h-4 w-4" style={{ color: accent }} /></div>
                  <p className="text-[color:var(--itw-text)] text-sm leading-relaxed">{o}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* DEEP FEATURES with secondary visual */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{num('02.')}{t('service.deepTitle')}</h2></ScrollReveal>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <div className={`space-y-4 ${flip ? '' : 'lg:order-2'}`}>
              {(tv(service.deepFeatures) || []).map((f, i) => (
                <ScrollReveal key={i} delay={i * 0.08}>
                  <div className="glass rounded-2xl p-6" style={{ borderColor: `${accent}22` }}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm" style={{ color: accent }}>{String(i + 1).padStart(2, '0')}</span>
                      <h3 className="font-display text-lg font-semibold text-[color:var(--itw-text)]">{f.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{f.text}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
            <ScrollReveal delay={0.1} className={flip ? '' : 'lg:order-1'}>
              <Tilt max={6}>
                <ServiceVisualSecondary slug={service.slug} accent={accent} />
              </Tilt>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FEATURES + SPECS */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{num('03.')}{t('service.whatWeOffer')}</h2></ScrollReveal>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tv(service.features).map((f, i) => (
                <ScrollReveal key={i} delay={i * 0.04}>
                  <div className="glass rounded-xl p-5 flex items-center gap-3 transition-transform duration-200 hover:-translate-y-0.5" style={{ borderColor: `${accent}22` }}>
                    <span className="font-mono text-xs" style={{ color: accent }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: accent }} />
                    <span className="text-[color:var(--itw-text)]">{f}</span>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
          <div>
            <ScrollReveal delay={0.1}>
              <div className="glass rounded-2xl p-6" style={{ borderColor: `${accent}33` }}>
                <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: accent }}>{t('service.specsTitle')}</div>
                <div className="space-y-3">
                  {(tv(service.specs) || []).map(([k, v], i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-[color:var(--itw-border)] pb-2 last:border-0">
                      <span className="text-[color:var(--itw-text-3)]">{k}</span>
                      <span className="font-mono text-[color:var(--itw-text)]">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{num('04.')}{t('service.useCasesTitle')}</h2></ScrollReveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {(tv(service.useCases) || []).map((u, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full relative overflow-hidden group">
                  <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(circle, ${accent}22, transparent 70%)` }} />
                  <div className="font-mono text-sm" style={{ color: accent }}>{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="mt-2 font-display text-lg font-semibold text-[color:var(--itw-text)]">{u.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{u.text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          {/* tech stack */}
          <ScrollReveal delay={0.1}>
            <div className="mt-10">
              <div className="text-xs uppercase tracking-widest text-[color:var(--itw-text-3)] font-mono mb-4">{t('service.stackTitle')}</div>
              <div className="flex flex-wrap gap-2.5">
                {(service.stack || []).map((s) => (
                  <span key={s} className="font-mono text-sm px-3 py-1.5 rounded-lg text-[color:var(--itw-text-2)]" style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}>{s}</span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* SERVICE FAQ */}
      <section className="relative z-10 py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal><h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)] mb-8">{num('05.')}{t('service.faqTitle')}</h2></ScrollReveal>
          <Accordion type="single" collapsible className="space-y-3" data-testid="service-faq">
            {(tv(service.faq) || []).map((f, i) => (
              <AccordionItem key={i} value={`sfaq-${i}`} className="glass rounded-xl px-5" style={{ borderColor: `${accent}22` }}>
                <AccordionTrigger className="text-left text-[color:var(--itw-text)] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[color:var(--itw-text-2)] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden bg-[color:var(--itw-surface)] p-10 sm:p-14" style={{ border: `1px solid ${accent}55`, boxShadow: `0 0 40px ${accent}22` }}>
              <div className="absolute inset-0 itw-grid opacity-40" />
              <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="max-w-xl">
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('service.ctaTitle')}</h2>
                  <p className="mt-3 text-[color:var(--itw-text-2)]">{t('service.ctaText')}</p>
                </div>
                <Button onClick={() => openOffer(service.slug)} data-testid="service-cta-bottom"
                  className="h-12 px-8 font-medium hover:opacity-90 transition-opacity rounded-full whitespace-nowrap text-[color:var(--itw-bg)]"
                  style={{ backgroundColor: accent, boxShadow: `0 0 28px ${accent}55` }}>
                  {t('nav.requestOffer')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* RELATED */}
      <section className="relative z-10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm uppercase tracking-widest text-[color:var(--itw-text-3)] mb-6 font-mono">{t('service.backToServices')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/leistungen/${r.slug}`} className="glass rounded-2xl p-5 flex items-center gap-3 transition-[border-color,transform] duration-200 hover:border-[color:var(--itw-border-strong)] hover:-translate-y-0.5" data-testid={`related-${r.slug}`}>
                <Icon name={r.icon} className="h-5 w-5" style={{ color: r.accent }} />
                <span className="text-[color:var(--itw-text)] font-medium">{tv(r.name)}</span>
                <ArrowRight className="h-4 w-4 text-[color:var(--itw-text-3)] ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
